import { useCallback, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import {
  Fingerprint,
  Plus,
  Trash2,
  Pencil,
  Check,
  X,
  AlertCircle,
} from 'lucide-react'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'

interface Passkey {
  id: string
  friendly_name?: string
  created_at: string
  last_used_at?: string
}

const webAuthnSupported = typeof window !== 'undefined' && !!window.PublicKeyCredential

function formatDate(iso: string) {
  try {
    return format(new Date(iso), 'd MMMM yyyy', { locale: fr })
  } catch {
    return iso
  }
}

export function PasskeySettings() {
  const [passkeys, setPasskeys] = useState<Passkey[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isRegistering, setIsRegistering] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  const loadPasskeys = useCallback(async () => {
    const { data, error } = await supabase.auth.passkey.list()
    if (error) {
      setError('Impossible de charger vos passkeys.')
    } else {
      setPasskeys(data ?? [])
    }
    setIsLoading(false)
  }, [])

  useEffect(() => {
    loadPasskeys()
  }, [loadPasskeys])

  const handleRegister = async () => {
    setError('')
    setSuccess('')
    setIsRegistering(true)

    try {
      const { data, error } = await supabase.auth.registerPasskey()
      if (error) throw error
      setSuccess(`Passkey « ${data?.friendly_name ?? 'nouveau'} » enregistré. Vous pouvez maintenant vous connecter sans mot de passe.`)
      await loadPasskeys()
    } catch (err) {
      const name = (err as { name?: string })?.name
      if (name === 'NotAllowedError' || name === 'AbortError') {
        // L'utilisateur a annulé la fenêtre WebAuthn
      } else if ((err as { code?: string })?.code === 'webauthn_credential_exists') {
        setError('Cet appareil est déjà enregistré sur votre compte.')
      } else if ((err as { code?: string })?.code === 'too_many_passkeys') {
        setError('Nombre maximum de passkeys atteint. Supprimez-en un avant d\'en ajouter.')
      } else {
        setError('Échec de l\'enregistrement du passkey.')
      }
    } finally {
      setIsRegistering(false)
    }
  }

  const handleRename = async (passkeyId: string) => {
    const friendlyName = renameValue.trim()
    if (!friendlyName) return
    setError('')

    const { error } = await supabase.auth.passkey.update({ passkeyId, friendlyName })
    if (error) {
      setError('Impossible de renommer ce passkey.')
    } else {
      setRenamingId(null)
      await loadPasskeys()
    }
  }

  const handleDelete = async (passkeyId: string) => {
    setError('')
    setSuccess('')

    const { error } = await supabase.auth.passkey.delete({ passkeyId })
    if (error) {
      setError('Impossible de supprimer ce passkey.')
    } else {
      setConfirmDeleteId(null)
      await loadPasskeys()
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <span className="flex items-center gap-2">
            <Fingerprint className="w-5 h-5 text-mint-600" />
            Passkeys
          </span>
        </CardTitle>
      </CardHeader>

      <div className="space-y-4">
        <p className="text-sm text-brown-600">
          Les passkeys remplacent votre mot de passe par une authentification
          biométrique (Touch ID, Face ID) ou une clé de sécurité. Ils sont
          résistants au phishing et synchronisés via votre trousseau
          (iCloud, Google, 1Password…).
        </p>

        {!webAuthnSupported && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            Ce navigateur ne supporte pas les passkeys (WebAuthn).
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {success && (
          <div className="flex items-center gap-2 p-3 bg-mint-50 border border-mint-200 rounded-lg text-mint-700 text-sm">
            <Check className="w-4 h-4 flex-shrink-0" />
            {success}
          </div>
        )}

        {/* Liste des passkeys */}
        {isLoading ? (
          <p className="text-sm text-brown-400">Chargement…</p>
        ) : passkeys.length === 0 ? (
          <p className="text-sm text-brown-400">
            Aucun passkey enregistré pour le moment.
          </p>
        ) : (
          <ul className="divide-y divide-beige-100">
            {passkeys.map((pk) => (
              <li key={pk.id} className="py-3 flex items-center gap-3">
                <Fingerprint className="w-5 h-5 text-brown-400 flex-shrink-0" />

                <div className="flex-1 min-w-0">
                  {renamingId === pk.id ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        placeholder="Nom du passkey"
                        maxLength={120}
                      />
                      <Button
                        size="sm"
                        onClick={() => handleRename(pk.id)}
                        leftIcon={<Check className="w-4 h-4" />}
                      >
                        OK
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setRenamingId(null)}
                        leftIcon={<X className="w-4 h-4" />}
                      >
                        Annuler
                      </Button>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-brown-900 truncate">
                        {pk.friendly_name || 'Passkey sans nom'}
                      </p>
                      <p className="text-xs text-brown-400">
                        Créé le {formatDate(pk.created_at)}
                        {pk.last_used_at && ` · Dernière utilisation le ${formatDate(pk.last_used_at)}`}
                      </p>
                    </>
                  )}
                </div>

                {renamingId !== pk.id && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setRenamingId(pk.id)
                        setRenameValue(pk.friendly_name ?? '')
                        setConfirmDeleteId(null)
                      }}
                      leftIcon={<Pencil className="w-4 h-4" />}
                    >
                      Renommer
                    </Button>
                    {confirmDeleteId === pk.id ? (
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => handleDelete(pk.id)}
                        leftIcon={<Trash2 className="w-4 h-4" />}
                      >
                        Confirmer ?
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setConfirmDeleteId(pk.id)}
                        leftIcon={<Trash2 className="w-4 h-4" />}
                      >
                        Supprimer
                      </Button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* Enregistrer un nouveau passkey */}
        {webAuthnSupported && (
          <div className="pt-2">
            <Button
              onClick={handleRegister}
              isLoading={isRegistering}
              leftIcon={<Plus className="w-4 h-4" />}
            >
              Ajouter un passkey
            </Button>
          </div>
        )}
      </div>
    </Card>
  )
}
